import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { Plus, X, Ticket, MessageSquare, Image as ImageIcon } from 'lucide-react';
import toast from 'react-hot-toast';

const API = process.env.REACT_APP_API_URL || '';

const PRIORITY_LABELS = { low: 'پایین', medium: 'متوسط', high: 'بالا' };
const PRIORITY_COLORS = {
  low: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
  medium: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30',
  high: 'bg-red-500/20 text-red-300 border-red-500/30'
};

const STATUS_LABELS = {
  open: 'باز',
  in_progress: 'در حال بررسی',
  waiting_user: 'پاسخ پشتیبانی',
  waiting_admin: 'در انتظار پشتیبانی',
  resolved: 'حل شده',
  closed: 'بسته شده'
};

const STATUS_COLORS = {
  open: 'bg-blue-500/20 text-blue-300 border-blue-500/30',
  in_progress: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30',
  waiting_user: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
  waiting_admin: 'bg-orange-500/20 text-orange-300 border-orange-500/30',
  resolved: 'bg-purple-500/20 text-purple-300 border-purple-500/30',
  closed: 'bg-gray-500/20 text-gray-400 border-gray-500/30'
};

const DEPT_LABELS = { management: 'مدیریت', technical: 'فنی و خرید' };

function CreateTicketModal({ onClose, onCreated }) {
  const { token } = useAuth();
  const fileInputRef = useRef(null);
  const [form, setForm] = useState({ subject: '', department: 'technical', priority: 'medium', message: '' });
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('حجم تصویر نباید بیشتر از ۵ مگابایت باشد');
      return;
    }
    setSelectedImage(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const removeSelectedImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.subject.trim() || (!form.message.trim() && !selectedImage)) {
      toast.error('موضوع و توضیحات یا تصویر را وارد کنید');
      return;
    }
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('subject', form.subject.trim());
      formData.append('department', form.department);
      formData.append('priority', form.priority);
      if (form.message.trim()) formData.append('message', form.message.trim());
      if (selectedImage) formData.append('image', selectedImage);

      const res = await fetch(`${API}/api/tickets`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      toast.success('تیکت با موفقیت ثبت شد');
      onCreated(data.ticketId);
    } catch (err) {
      toast.error(err.message || 'خطا در ثبت تیکت');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        className="relative w-full max-w-lg bg-gray-900 border border-white/10 rounded-2xl shadow-2xl overflow-hidden text-white"
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between bg-gradient-to-r from-emerald-500/10 to-cyan-500/10">
          <h2 className="text-lg font-bold text-white flex items-center gap-2">
            <Ticket className="w-5 h-5 text-emerald-400" />
            <span>ثبت تیکت جدید</span>
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* موضوع */}
          <div>
            <label className="block text-sm text-gray-400 mb-1.5">موضوع تیکت *</label>
            <input
              type="text"
              value={form.subject}
              onChange={e => setForm(f => ({ ...f, subject: e.target.value }))}
              placeholder="مثال: مشکل در خرید رنک"
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white placeholder-gray-500 focus:outline-none focus:border-emerald-500 transition-colors text-sm"
            />
          </div>

          {/* بخش و اولویت */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1.5">بخش *</label>
              <select
                value={form.department}
                onChange={e => setForm(f => ({ ...f, department: e.target.value }))}
                className="w-full bg-gray-800 border border-white/10 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:border-emerald-500 transition-colors text-sm"
              >
                <option value="technical">فنی و خرید</option>
                <option value="management">مدیریت</option>
              </select>
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1.5">اولویت</label>
              <select
                value={form.priority}
                onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}
                className="w-full bg-gray-800 border border-white/10 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:border-emerald-500 transition-colors text-sm"
              >
                <option value="low">پایین</option>
                <option value="medium">متوسط</option>
                <option value="high">بالا</option>
              </select>
            </div>
          </div>

          {/* پیام */}
          <div>
            <label className="block text-sm text-gray-400 mb-1.5">توضیحات *</label>
            <textarea
              value={form.message}
              onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
              rows={3}
              placeholder="مشکل یا درخواست خود را شرح دهید..."
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white placeholder-gray-500 focus:outline-none focus:border-emerald-500 transition-colors resize-none text-sm"
            />
          </div>

          {/* پیوست تصویر */}
          <div>
            <label className="block text-sm text-gray-400 mb-1.5">تصویر پیوست (اختیاری)</label>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              accept="image/*"
              className="hidden"
            />
            {imagePreview ? (
              <div className="flex items-center gap-3 bg-white/5 p-2 rounded-xl border border-white/10">
                <img src={imagePreview} alt="پیش‌نمایش" className="w-12 h-12 object-cover rounded-lg" />
                <span className="text-xs text-emerald-400 flex-1 truncate font-medium">{selectedImage?.name}</span>
                <button
                  type="button"
                  onClick={removeSelectedImage}
                  className="p-1 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="w-full py-2.5 bg-white/5 border border-dashed border-white/20 hover:border-emerald-500/50 rounded-xl text-gray-400 hover:text-emerald-400 transition-all text-xs flex items-center justify-center gap-2"
              >
                <ImageIcon className="w-4 h-4" />
                <span>انتخاب تصویر (حداکثر ۵ مگابایت)</span>
              </button>
            )}
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2.5 border border-white/10 rounded-xl text-gray-400 hover:text-white hover:border-white/30 transition-all text-sm"
            >
              انصراف
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 py-2.5 bg-gradient-to-r from-emerald-500 to-cyan-500 rounded-xl text-white font-medium hover:opacity-90 transition-opacity disabled:opacity-50 text-sm"
            >
              {loading ? 'در حال ثبت...' : 'ثبت تیکت'}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}

export default function Tickets() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [showCreate, setShowCreate] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    if (!token) { navigate('/login'); return; }
  }, [token, navigate]);

  const fetchTickets = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 5 });
      if (filterStatus !== 'all') params.append('status', filterStatus);
      const res = await fetch(`${API}/api/tickets?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setTickets(data.tickets || []);
      setTotalPages(data.totalPages || 1);
    } catch {
      toast.error('خطا در بارگذاری تیکت‌ها');
    } finally {
      setLoading(false);
    }
  }, [token, filterStatus, page]);

  useEffect(() => { fetchTickets(); }, [fetchTickets]);

  const handleCreated = (ticketId) => {
    setShowCreate(false);
    navigate(`/tickets/${ticketId}`);
  };

  const formatDate = (date) => {
    try {
      if (!date) return '';
      let dateObj;
      if (typeof date === 'string') {
        const normalized = date.includes('T') || date.includes('Z') ? date : date.replace(' ', 'T') + 'Z';
        dateObj = new Date(normalized);
      } else {
        dateObj = new Date(date);
      }
      if (isNaN(dateObj.getTime())) return '';
      return new Intl.DateTimeFormat('fa-IR', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'Asia/Tehran'
      }).format(dateObj);
    } catch {
      return '';
    }
  };

  return (
    <div className="min-h-screen bg-[#050811] pt-20 pb-12 text-white" dir="rtl">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
        >
          <div>
            <h1 className="text-2xl font-bold text-white mb-1 flex items-center gap-2">
              <Ticket className="w-6 h-6 text-emerald-400" />
              <span>تیکت‌های پشتیبانی</span>
            </h1>
            <p className="text-gray-400 text-sm">برای هر مشکل یا سوال تیکت بزنید</p>
          </div>
          <button
            onClick={() => setShowCreate(true)}
            className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-emerald-500 to-cyan-500 text-white rounded-xl font-medium hover:opacity-90 transition-all shadow-lg shadow-emerald-500/20 text-sm"
          >
            <Plus className="w-4 h-4" />
            <span>تیکت جدید</span>
          </button>
        </motion.div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-6">
          {[
            { key: 'all', label: 'همه' },
            { key: 'open', label: 'باز' },
            { key: 'in_progress', label: 'در بررسی' },
            { key: 'waiting_user', label: 'پاسخ پشتیبانی' },
            { key: 'waiting_admin', label: 'منتظر پشتیبانی' },
            { key: 'resolved', label: 'حل شده' },
            { key: 'closed', label: 'بسته' }
          ].map(f => (
            <button
              key={f.key}
              onClick={() => { setFilterStatus(f.key); setPage(1); }}
              className={`px-4 py-2 rounded-xl text-sm font-medium border transition-all ${
                filterStatus === f.key
                  ? 'bg-emerald-500/20 border-emerald-500/50 text-emerald-300'
                  : 'bg-white/5 border-white/10 text-gray-400 hover:border-white/20'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Tickets List */}
        {loading && tickets.length === 0 ? (
          <div className="flex justify-center py-20">
            <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : tickets.length === 0 ? (
          <motion.div
            initial={{ opacity: 1 }}
            animate={{ opacity: 1 }}
            className="text-center py-20 text-gray-500"
          >
            <MessageSquare className="w-12 h-12 mx-auto mb-3 text-gray-600" />
            <p className="text-lg mb-2 text-gray-400">تیکتی یافت نشد</p>
            <p className="text-sm text-gray-500">برای ثبت تیکت جدید روی دکمه بالا کلیک کنید</p>
          </motion.div>
        ) : (
          <div className="space-y-3">
            {tickets.map((ticket) => (
              <div
                key={ticket.id}
                onClick={() => navigate(`/tickets/${ticket.id}`)}
                className="group cursor-pointer bg-white/5 border border-white/10 rounded-2xl p-5 hover:bg-white/8 hover:border-white/20 transition-all"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <span className="text-sm text-gray-500">#{ticket.id}</span>
                      <span className="bg-purple-500/20 text-purple-300 border border-purple-500/30 text-xs px-2 py-0.5 rounded-full">
                        {DEPT_LABELS[ticket.department] || ticket.department}
                      </span>
                    </div>
                    <h3 className="text-white font-medium truncate group-hover:text-emerald-300 transition-colors">
                      {ticket.subject}
                    </h3>
                    <p className="text-xs text-gray-500 mt-1">{formatDate(ticket.createdAt)}</p>
                  </div>

                  <div className="flex flex-col items-end gap-2 shrink-0">
                    <span className={`text-xs px-2.5 py-1 rounded-full border ${STATUS_COLORS[ticket.status] || STATUS_COLORS.open}`}>
                      {STATUS_LABELS[ticket.status] || ticket.status}
                    </span>
                    <span className={`text-xs px-2.5 py-1 rounded-full border ${PRIORITY_COLORS[ticket.priority]}`}>
                      {PRIORITY_LABELS[ticket.priority]}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center gap-2 mt-6">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
              <button
                key={p}
                onClick={() => setPage(p)}
                className={`w-9 h-9 rounded-xl text-sm transition-all ${
                  page === p
                    ? 'bg-emerald-500 text-white'
                    : 'bg-white/5 border border-white/10 text-gray-400 hover:border-white/20'
                }`}
              >
                {p}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Create Modal */}
      <AnimatePresence>
        {showCreate && <CreateTicketModal onClose={() => setShowCreate(false)} onCreated={handleCreated} />}
      </AnimatePresence>
    </div>
  );
}
